// #region Imports
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SaveIcon from "@mui/icons-material/Save";
import { TextFieldProps } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import LoadingButton from "_shared/_views/loading/loading-button";
import EmailAddress from "_shared/email-addresses/email-address";
import EmailAddressSchema from "_shared/email-addresses/email-address-schema";
import Guid from "_shared/guids/guid";
import { Currency } from "_shared/money/currencies/currency";
import PhoneNumber from "_shared/phone-numbers/phone-number";
import UrlList from "_shared/urls/url-list";
import * as Config from "config";
import { E164Number } from "libphonenumber-js";
import AvatarUpload from "marketplace/_views/avatars/avatar-upload";
import ProfileCategoriesSelect from "marketplace/_views/profiles/profile-categories-select";
import {
  default as VisibilityToggleSwitch,
  buildVisibilityConfirmMessage,
  buildVisibilityConfirmTitle,
  buildVisibilityErrorMessage,
  buildVisibilitySuccessMessage,
} from "marketplace/_views/profiles/visibility-toggle-switch";
import ResumeUpload from "marketplace/_views/resume-upload";
import {
  getIndividualAvatar,
  getIndividualById,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { individualsApi, useGetIndividualAvatarQuery, useGetIndividualByIdQuery } from "marketplace/individuals/_api/individual-api";
import IndividualProfileEditName from "marketplace/individuals/profiles/_views/forms/edit/fields/individual-profile-name-field";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import InsuranceInfo from "marketplace/insurance-info/insurance-info";
import RegistrationNumber from "marketplace/registration-number/registration-number";
import BillingRateInfo from "marketplace/teams/fee-schedules/rates/billing-rate-info";
import TechnicalBackgroundInfo from "marketplace/technical-background-info/technical-background-info";
import { enqueueSnackbar } from "notistack";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { NumberFormatValues, NumericFormat } from "react-number-format";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useSelector } from "react-redux";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(() => ({
  display: "grid",
  gridTemplateRows: "repeat(5, auto)",
  width: "100%",
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    "& > *": {
      margin: 0,
      padding: theme.spacing(0, 0, 3, 0),
      width: "100%",
    },
    "&:not(last-child)": {
      paddingBottom: theme.spacing(0.5),
    },
  },
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "row",
  gridColumn: "1 / auto",
  gridRowStart: "1",
  justifyContent: "center",
  padding: theme.spacing(0, 0, 4, 0),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const ContentContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    columnCount: 1,
  },
  [theme.breakpoints.up("md")]: {
    columnCount: 2,
  },
  [theme.breakpoints.up("lg")]: {
    columnCount: 3,
  },
  columnFill: "balance",
  columnGap: theme.spacing(4),
  gridColumn: "1 / auto",
  gridRowStart: "2",
  paddingTop: theme.spacing(0.75),
}));
const FieldContainer = styled("div")(() => ({
  display: "inline-block",
  width: "100%",
}));
const CheckboxContainer = styled("div")(({ theme }) => ({
  display: "inline-block",
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const GridContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(2),
}));
const Credentials = styled("section")(({ theme }) => ({
  gridColumn: "1 / span 3",
  gridRowStart: "3",
  marginTop: theme.spacing(1),
}));
const CredentialsTextField = styled(TextField)(() => ({
  width: "100%",
}));
const AvatarNameContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
}));
const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const InsuredAmountField = styled(NumericFormat)<TextFieldProps>(({ theme }) => ({
  marginBottom: theme.spacing(2),
})) as typeof NumericFormat;
const BillingRateField = styled(NumericFormat)<TextFieldProps>(({ theme }) => ({
  marginBottom: theme.spacing(2),
})) as typeof NumericFormat;
const BillingUnitField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const ContactInfoContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  flexDirection: "row",
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const ContactDetails = styled("div")(() => ({
  flex: 1,
  width: "100%",
}));
const PhoneField = styled(PhoneInputWithCountrySelect)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: "100%",
}));
const EmailField = styled(TextField)(() => ({
  width: "100%",
}));
const OnCallCheckbox = styled(FormControlLabel)(() => ({
  margin: 0,
  position: "relative",
}));
const HelpIcon = styled(HelpOutlineIcon)(({ theme }) => ({
  color: "rgba(0, 0, 0, 0.5)",
  cursor: "help",
  marginLeft: theme.spacing(0.75),
}));
const TitleContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
}));
const ProfileDetails = styled("section")(({ theme }) => ({
  gridColumn: "1 / span 3",
  gridRowStart: "4",
  marginTop: theme.spacing(1),
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  bottom: 0,
  gridColumn: "1",
  gridRowStart: "5",
  justifySelf: "flex-start",
  padding: theme.spacing(2, 0),
  position: "sticky",
  width: "100%",
  zIndex: 10,
}));
// #endregion

const TextFieldComponent = forwardRef<HTMLInputElement, TextFieldProps>(function TextFieldComponent(props, ref) {
  return <TextField {...props} inputRef={ref} />;
});

export interface EditIndividualProfileProps {
  individualId?: Guid;
  isVisible?: boolean;
  initialProfile?: IndividualProfile;
  onFormDirty?: (isDirty?: boolean) => void;
  onSave?: (profile: IndividualProfile) => void;
}

export default function EditIndividualProfile(props: Readonly<EditIndividualProfileProps>) {
  const { onFormDirty, onSave } = props;

  // #region State
  const [isSaving, setIsSaving] = useState(false);
  const [isVisible, setIsVisible] = useState<boolean>(props.isVisible ?? false);
  const [isVisibilitySaving, setIsVisibilitySaving] = useState<boolean>(false);
  const [individualId] = useState<Guid | undefined>(props.individualId);
  const [currentProfile, setCurrentProfile] = useState<IndividualProfile | undefined>(props.initialProfile);
  const [updatedProfile, setUpdatedProfile] = useState<IndividualProfile | undefined>(props.initialProfile);
  const [updatedAvatar, setUpdatedAvatar] = useState<File | undefined>();
  const [updatedResume, setUpdatedResume] = useState<File | null | undefined>(undefined);
  const [formEmail, setFormEmail] = useState<string>("");
  const [formPhoneNumber, setFormPhoneNumber] = useState<E164Number | undefined>();
  const [formSchool, setFormSchool] = useState<string>("");
  const [formDegree, setFormDegree] = useState<string>("");
  const [formRegionsRegistered, setFormRegionsRegistered] = useState<string[]>([]);
  const [formRegionsLicensed, setFormRegionsLicensed] = useState<string[]>([]);
  const [formNumberOfApplicationsDraftedProsecuted, setFormNumberOfApplicationsDraftedProsecuted] =
    useState<string>("");
  const [formNumberOfAllowedApplications, setFormNumberOfAllowedApplications] = useState<string>("");
  const [formBarAdmissionYear, setFormBarAdmissionYear] = useState<string>("");
  const [formProfessionalRegistration, setFormProfessionalRegistration] = useState<string>("");
  const [formTechnicalBackground, setFormTechnicalBackground] = useState<string>("");
  const [formProfessionalPublications, setFormProfessionalPublications] = useState<string[]>([]);
  const [formSampleApplications, setFormSampleApplications] = useState<string[]>([]);
  const [formLinks, setFormLinks] = useState<string[]>([]);
  const [formTechnicalLanguages, setFormTechnicalLanguages] = useState<string[]>([]);
  const [formFluentLanguages, setFormFluentLanguages] = useState<string[]>([]);
  const [formInsuredAmount, setFormInsuredAmount] = useState<number>(0);
  const [formBillingRate, setFormBillingRate] = useState<string>("");
  const [formBillingUnit, setFormBillingUnit] = useState<string>("");
  const [formValidated, setFormValidated] = useState(false);
  const [profileHash, setProfileHash] = useState<string>("");

  // #region Hooks
  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();

  // #region Individual Selectors
  const individual = useSelector(getIndividualById(individualId));
  const { isLoading: isLoadingIndividual, isError: errorLoadingIndividual } = useGetIndividualByIdQuery(
    { individualId: individualId?.value ?? "" },
    { skip: !individualId || !!individual },
  );

  // #region Avatar Selectors
  const avatar = useSelector(getIndividualAvatar(individualId ?? null));
  const { isLoading: isLoadingAvatar } = useGetIndividualAvatarQuery(
    { individualId: individualId?.value ?? "" },
    { skip: !individualId || !individual?.profile?.avatarId || !!avatar },
  );

  const validateProfile = (profile: IndividualProfile) => {
    if (!profile) return;
    const hasCategories: boolean = (profile.categories?.length ?? 0) > 0;
    const hasName: boolean = profile.firstName.length > 0 && profile.lastName.length > 0;
    setFormValidated(hasCategories && hasName);
  };


  const dirty = useMemo<boolean>(() => {
    if (!updatedProfile) {
      onFormDirty?.(false);
      return false;
    }
    const hash = JSON.stringify(updatedProfile.toJSON());
    const isDirty = hash !== profileHash || !!updatedAvatar || !!updatedResume;
    onFormDirty?.(isDirty);
    return isDirty;
  // We cannot guarantee onFormDirty being stable, if it changes, change the key to rerender
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileHash, updatedAvatar, updatedProfile, updatedResume]);

  const canSave = useMemo<boolean>(() =>
    !isLoadingIndividual &&
    !isLoadingAvatar &&
    !isSaving &&
    dirty &&
    formValidated &&
    (formEmail.length === 0 || EmailAddressSchema.isValidJSON(formEmail)),
  [dirty, formEmail, formValidated, isLoadingAvatar, isLoadingIndividual, isSaving]);

  function setFormValues(profile: IndividualProfile) {
    setFormEmail(profile.email?.value ?? "");
    setFormPhoneNumber(profile.phoneNumber?.toE164Number());
    setFormSchool(profile.school ?? "");
    setFormDegree(profile.degree ?? "");
    setFormProfessionalRegistration(profile.professionalRegistration?.value ?? "");
    setFormTechnicalBackground(profile.technicalBackground?.value ?? "");
    setFormProfessionalPublications(profile.professionalPublications ?? []);
    setFormLinks(profile.links ?? []);
    setFormTechnicalLanguages(profile.technicalLanguages ?? []);
    setFormFluentLanguages(profile.fluentLanguages ?? []);
    setFormRegionsRegistered(profile.regionsRegistered ?? []);
    setFormRegionsLicensed(profile.regionsLicensed ?? []);
    setFormNumberOfApplicationsDraftedProsecuted(profile.numberOfApplicationsDraftedProsecuted?.toString() ?? "");
    setFormNumberOfAllowedApplications(profile.numberOfAllowedApplications?.toString() ?? "");
    setFormBarAdmissionYear(profile.barAdmissionYear?.toString() ?? "");
    setFormInsuredAmount(profile.insured?.insuredAmount ?? 0);
    setFormBillingRate(profile.billingRate?.billingRate ?? "");
    setFormBillingUnit(profile.billingRate?.billingUnit ?? "");
    setFormSampleApplications(profile.sampleApplications ?? []);
  }

  // #region Lifecycle
  useEffect(() => {
    if (!individual) return;
    if (!individual.profile) throw new Error("Individual profile not found");

    const hash = JSON.stringify(individual.profile.toJSON());
    setProfileHash(hash);

    setCurrentProfile(individual.profile);
    setFormValues(individual.profile);
    setIsVisible(individual.isVisible ?? false);

    setUpdatedProfile(individual.profile);
    validateProfile(individual.profile);
  }, [individual]);

  useEffect(() => {
    if (errorLoadingIndividual) {
      enqueueSnackbar("Profile currently unavailable.", { variant: "warning" });
    }
  }, [errorLoadingIndividual]);
  // #endregion

  async function handleSaveButtonClick() {
    if (individualId) await updateProfile();
  }

  async function updateProfile() {
    if (!individualId || !currentProfile || !updatedProfile) return;

    try {
      setIsSaving(true);
      if (updatedAvatar) {
        await createAvatar(individualId, updatedAvatar);
        setUpdatedAvatar(undefined);
      }
      if (updatedResume) {
        await createResume(individualId, updatedResume);
        setUpdatedResume(undefined);
      }
      else if (updatedResume === null)
      {
        await dispatch(individualsApi.endpoints.deleteIndividualResume.initiate({ individualId: individualId.value })).unwrap();
      }
      const returnedProfileJson = await dispatch(
        individualsApi.endpoints.updateIndividual.initiate({
          individualId: individualId.value,
          isVisible,
          originalProfile: currentProfile,
          updatedProfile,
        }),
      ).unwrap();
      const returnedProfile = IndividualProfile.fromJSON(returnedProfileJson);

      setCurrentProfile(returnedProfile);
      setUpdatedProfile(returnedProfile);

      enqueueSnackbar("Profile changes saved", { variant: "success" });

      onSave?.(returnedProfile);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Unable to update profile", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleImmediateVisibilityChange(nextIsVisible: boolean) {
    if (!individualId || !updatedProfile) return;

    const profileName = `${updatedProfile.firstName} ${updatedProfile.lastName}`.trim() || "this";
    const response = await confirm({
      title: buildVisibilityConfirmTitle(nextIsVisible),
      message: buildVisibilityConfirmMessage(profileName, nextIsVisible),
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsVisibilitySaving(true);
      await dispatch(
        individualsApi.endpoints.updateIndividual.initiate({
          individualId: individualId.value,
          isVisible: nextIsVisible,
        }),
      ).unwrap();

      setIsVisible(nextIsVisible);
      enqueueSnackbar(buildVisibilitySuccessMessage(profileName, nextIsVisible), { variant: "info" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(buildVisibilityErrorMessage(nextIsVisible), { variant: "error" });
    } finally {
      setIsVisibilitySaving(false);
    }
  }

  async function createAvatar(individualId: Guid, avatar: File) {
    if (!individualId || !avatar) return;

    const result = await dispatch(individualsApi.endpoints.createIndividualAvatar.initiate({ individualId: individualId.value, file: avatar }));
    if ("error" in result) {
      console.error("Unable to create avatar");
      enqueueSnackbar("Unable to create avatar", { variant: "error" });
    }
  }

  async function createResume(individualId: Guid, resume: File) {
    if (!individualId || !resume) return;

    const result = await dispatch(individualsApi.endpoints.createIndividualResume.initiate({ individualId: individualId.value, file: resume }));
    if ("error" in result) {
      console.error("Unable to create avatar");
      enqueueSnackbar("Unable to create avatar", { variant: "error" });
    }
  }

  function hideIfNotIPAttorney() {
    return {
      display: updatedProfile?.categories?.includes("IPAttorney") ? "inline-flex" : "none",
    };
  }

  return (
    <MainContainer>
      <HeaderContainer>
        <AvatarNameContainer>
          <AvatarUpload
            accept="image/jpeg, image/png"
            avatar={updatedAvatar ?? avatar}
            onUpload={(updatedAvatar) => setUpdatedAvatar(updatedAvatar)}
          />
          <IndividualProfileEditName
            isLoading={isLoadingIndividual}
            firstName={updatedProfile?.firstName ?? ""}
            lastName={updatedProfile?.lastName ?? ""}
            onNameChange={(firstName: string, lastName: string) => {
              if (!updatedProfile || (firstName === updatedProfile.firstName && lastName === updatedProfile.lastName))
                return;

              const newProfile = new IndividualProfile({
                ...updatedProfile,
                firstName,
                lastName,
              });
              setUpdatedProfile(newProfile);
              validateProfile(newProfile);
            }}
          />
        </AvatarNameContainer>
        <ProfileCategoriesSelect
          options={Config.IndividualProfileTypes}
          categories={updatedProfile?.categories ?? []}
          onChange={async (updatedCategories: string[]) => {
            if (!updatedProfile) return;
            if (updatedCategories === updatedProfile.categories) return;
            const newProfile = new IndividualProfile({
              ...updatedProfile,
              categories: updatedCategories,
            });
            setUpdatedProfile(newProfile);
            validateProfile(newProfile);
          }}
        />
      </HeaderContainer>
      <ContentContainer>
        <StyledTextField
          id="registrationNumber"
          variant="outlined"
          size="small"
          sx={hideIfNotIPAttorney()}
          label="Professional Registration #"
          value={formProfessionalRegistration}
          onChange={(event) => {
            setFormProfessionalRegistration(event.target.value);
            if (!updatedProfile) return;

            const registrationNumberValue = new RegistrationNumber(event.target.value);
            if (registrationNumberValue === updatedProfile.professionalRegistration) return;

            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                professionalRegistration: registrationNumberValue,
              }),
            );
          }}
        />
        <StyledAutocomplete
          id="patentRegions"
          multiple
          size="small"
          sx={hideIfNotIPAttorney()}
          value={formRegionsRegistered ?? []}
          options={Config.countries.map((x) => x.name)}
          renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Regions Registered" />}
          onChange={(_event, value, _reason) => {
            if (!Array.isArray(value)) return;

            setFormRegionsRegistered(value as string[]);

            if (!updatedProfile) return;
            if (value === updatedProfile.regionsRegistered) return;

            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                regionsRegistered: value as string[],
              }),
            );
          }}
        />
        <StyledTextField
          id="technicalBackground"
          variant="outlined"
          size="small"
          label="Technical Background"
          value={formTechnicalBackground ?? ""}
          slotProps={{
            inputLabel: {
              shrink: formTechnicalBackground !== "",
            },
          }}
          onChange={(event) => {
            setFormTechnicalBackground(event.target.value);
            if (!updatedProfile) return;
            if (event.target.value === updatedProfile.technicalBackground?.value) return;
            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                technicalBackground: new TechnicalBackgroundInfo(event.target.value),
              }),
            );
          }}
        />
        <StyledTextField
          id="school"
          variant="outlined"
          size="small"
          label="Undergraduate School / Law School"
          value={formSchool ?? ""}
          slotProps={{
            inputLabel: {
              shrink: Boolean(updatedProfile?.school),
            },
          }}
          onChange={(event) => {
            setFormSchool(event.target.value);
            if (!updatedProfile) return;
            if (event.target.value === updatedProfile.school) return;
            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                school: event.target.value,
              }),
            );
          }}
        />
        <StyledTextField
          id="degree"
          variant="outlined"
          size="small"
          label="Degree(s)"
          value={formDegree ?? ""}
          slotProps={{
            inputLabel: {
              shrink: Boolean(updatedProfile?.degree),
            },
          }}
          onChange={(event) => {
            setFormDegree(event.target.value);
            if (!updatedProfile) return;
            if (event.target.value === updatedProfile.degree) return;
            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                degree: event.target.value,
              }),
            );
          }}
        />
        <StyledTextField
          id="numberDrafted"
          variant="outlined"
          type="number"
          size="small"
          sx={hideIfNotIPAttorney()}
          label="Number Applications Drafted / Prosecuted"
          slotProps={{
            inputLabel: {
              shrink: formNumberOfApplicationsDraftedProsecuted !== "",
            },
          }}
          value={formNumberOfApplicationsDraftedProsecuted ?? ""}
          onChange={(event) => {
            setFormNumberOfApplicationsDraftedProsecuted(event.target.value);
            if (!updatedProfile) return;
            let newValue: number | undefined;
            if (event.target.value !== "") newValue = Number.parseInt(event.target.value);
            if (newValue === updatedProfile.numberOfApplicationsDraftedProsecuted) return;
            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                numberOfApplicationsDraftedProsecuted: newValue,
              }),
            );
          }}
        />
        <StyledTextField
          id="numberAllowedApplications"
          variant="outlined"
          type="number"
          size="small"
          sx={hideIfNotIPAttorney()}
          label="Number Allowed Applications"
          slotProps={{
            inputLabel: {
              shrink: formNumberOfAllowedApplications !== "",
            },
          }}
          value={formNumberOfAllowedApplications ?? ""}
          onChange={(event) => {
            setFormNumberOfAllowedApplications(event.target.value);
            if (!updatedProfile) return;
            let newValue: number | undefined;
            if (event.target.value !== "") newValue = Number.parseInt(event.target.value);
            if (newValue === updatedProfile.numberOfAllowedApplications) return;
            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                numberOfAllowedApplications: newValue,
              }),
            );
          }}
        />
        <StyledTextField
          id="yearAdmitted"
          variant="outlined"
          size="small"
          sx={hideIfNotIPAttorney()}
          type="number"
          label="Year Of Bar Admission"
          slotProps={{
            inputLabel: {
              shrink: formBarAdmissionYear !== "",
            },
          }}
          value={formBarAdmissionYear ?? ""}
          onChange={(event) => {
            setFormBarAdmissionYear(event.target.value);
            if (!updatedProfile) return;
            let newValue: number | undefined;
            if (event.target.value !== "") newValue = Number.parseInt(event.target.value);
            if (newValue === updatedProfile.barAdmissionYear) return;
            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                barAdmissionYear: newValue,
              }),
            );
          }}
        />
        <StyledAutocomplete
          id="fluentLanguages"
          multiple
          size="small"
          onChange={(_event, value, _reason) => {
            if (!Array.isArray(value)) return;
            setFormFluentLanguages(value as string[]);
            if (!updatedProfile) return;
            if (value === updatedProfile.fluentLanguages) return;
            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                fluentLanguages: value as string[],
              }),
            );
          }}
          value={formFluentLanguages ?? []}
          options={Config.languages.map((option) => option.name)}
          renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Fluent Languages" />}
        />
        <StyledAutocomplete
          id="technicalLanguages"
          size="small"
          multiple
          onChange={(_event, value, _reason) => {
            if (!Array.isArray(value)) return;
            setFormTechnicalLanguages(value as string[]);
            if (!updatedProfile) return;
            if (value === updatedProfile.technicalLanguages) return;
            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                technicalLanguages: value as string[],
              }),
            );
          }}
          value={formTechnicalLanguages ?? []}
          options={Config.languages.map((option) => option.name)}
          renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Technical Languages" />}
        />
        <StyledAutocomplete
          id="states"
          size="small"
          sx={hideIfNotIPAttorney()}
          multiple
          onChange={(_event, value, _reason) => {
            if (!Array.isArray(value)) return;
            setFormRegionsLicensed(value as string[]);
            if (!updatedProfile) return;
            if (value === updatedProfile.regionsLicensed) return;
            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                regionsLicensed: value as string[],
              }),
            );
          }}
          value={formRegionsLicensed ?? []}
          options={Config.states.map((option) => option.name)}
          renderInput={(params) => <TextField {...params} variant="outlined" placeholder="States Licensed In" />}
        />
        <GridContainer>
          <InsuredAmountField
            id="insuredAmount"
            customInput={TextField}
            size="small"
            sx={{
              display: updatedProfile?.categories?.includes("IPAttorney") ? "inline-flex" : "none",
            }}
            variant="outlined"
            label="Insured Amount"
            value={formInsuredAmount ?? 0}
            prefix="$"
            thousandSeparator=","
            onValueChange={(value: NumberFormatValues) => {
              setFormInsuredAmount(value.floatValue ?? 0);
              if (!updatedProfile) return;
              if (value.floatValue === updatedProfile.insured?.insuredAmount) return;
              setUpdatedProfile(
                IndividualProfile.create({
                  ...updatedProfile,
                  insured: InsuranceInfo.create({
                    insuredAmount: value.floatValue ?? 0,
                    currency: Currency.USD,
                  }),
                }),
              );
            }}
          />
        </GridContainer>
        <GridContainer>
          <BillingRateField
            id="billingRate"
            customInput={TextField}
            variant="outlined"
            label="Billing Rate"
            size="small"
            sx={{
              display: updatedProfile?.categories?.includes("IPAttorney") ? "inline-flex" : "none",
            }}
            value={formBillingRate}
            prefix="$"
            thousandSeparator=","
            onValueChange={(value: NumberFormatValues) => {
              setFormBillingRate(value.value);
              if (!updatedProfile) return;
              if (value.floatValue === updatedProfile.billingRate?.billingRate) return;
              setUpdatedProfile(
                new IndividualProfile({
                  ...updatedProfile,
                  billingRate: new BillingRateInfo({
                    billingRate: value.value ?? "0",
                    billingUnit: updatedProfile.billingRate?.billingUnit ?? "",
                  }),
                }),
              );
            }}
          />
          <BillingUnitField
            id="billingUnit"
            variant="outlined"
            label="Billing Unit"
            size="small"
            sx={{
              display: updatedProfile?.categories?.includes("IPAttorney") ? "inline-flex" : "none",
            }}
            value={formBillingUnit}
            onChange={(event) => {
              setFormBillingUnit(event.target.value);
              if (!updatedProfile) return;
              if (event.target.value === updatedProfile.billingRate?.billingUnit) return;
              setUpdatedProfile(
                new IndividualProfile({
                  ...updatedProfile,
                  billingRate: new BillingRateInfo({
                    billingRate: updatedProfile.billingRate?.billingRate ?? "0",
                    billingUnit: event.target.value ?? "",
                  }),
                }),
              );
            }}
          />
        </GridContainer>
        <ContactInfoContainer>
          <ContactDetails>
            <PhoneField
              id="phoneNumber"
              international
              defaultCountry="US"
              countryCallingCodeEditable={false}
              size="small"
              variant="outlined"
              value={formPhoneNumber}
              label="Phone Number"
              autoComplete="tel"
              inputComponent={TextFieldComponent}
              onChange={(phoneNumber) => {
                setFormPhoneNumber(phoneNumber);
                if (!updatedProfile) return;
                if (phoneNumber?.toString() === updatedProfile.phoneNumber?.value?.toString()) return;
                setUpdatedProfile(
                  new IndividualProfile({
                    ...updatedProfile,
                    phoneNumber: phoneNumber ? new PhoneNumber(phoneNumber) : undefined,
                  }),
                );
              }}
            />
            <EmailField
              id="email"
              size="small"
              variant="outlined"
              value={formEmail ?? ""}
              label="Email Address"
              autoComplete="email"
              error={formEmail.length > 0 && !EmailAddressSchema.isValidJSON(formEmail)}
              onChange={(event) => {
                setFormEmail(event.target.value);
                if (!updatedProfile) return;
                if (event.target.value === formEmail) return;
                setUpdatedProfile(
                  new IndividualProfile({
                    ...updatedProfile,
                    email: EmailAddressSchema.isValidJSON(event.target.value)
                      ? new EmailAddress(event.target.value)
                      : undefined,
                  }),
                );
              }}
            />
          </ContactDetails>
          <OnCallCheckbox
            id="onCallCheckbox"
            control={
              <Checkbox
                color="primary"
                checked={
                  (updatedProfile?.isOnCall &&
                    (Boolean(updatedProfile.phoneNumber) || Boolean(updatedProfile.email))) ??
                  false
                }
              />
            }
            label="On-Call"
            labelPlacement="end"
            disabled={!updatedProfile?.phoneNumber && !updatedProfile?.email}
            onChange={(event) => {
              if (!updatedProfile) return;
              const isChecked: boolean = (event.target as HTMLInputElement).checked;
              if (isChecked === updatedProfile.isOnCall) return;
              setUpdatedProfile(
                new IndividualProfile({
                  ...updatedProfile,
                  isOnCall: isChecked,
                }),
              );
            }}
          />
        </ContactInfoContainer>
        <CheckboxContainer>
          <FormControlLabel
            id="diversityCandidate"
            control={
              <Checkbox
                value="diversityCandidate"
                color="primary"
                checked={updatedProfile?.potentialDiversityCandidate ?? false}
              />
            }
            label={<Typography>Potential Diversity Candidate</Typography>}
            labelPlacement="end"
            onChange={(event) => {
              if (!updatedProfile) return;
              const isChecked: boolean = (event.target as HTMLInputElement).checked;
              if (isChecked === updatedProfile.potentialDiversityCandidate) return;
              setUpdatedProfile(
                new IndividualProfile({
                  ...updatedProfile,
                  potentialDiversityCandidate: isChecked,
                }),
              );
            }}
          />
          <FormControlLabel
            id="collaborate"
            control={
              <Checkbox value="willingToCollaborate" color="primary" checked={updatedProfile?.collaborates ?? false} />
            }
            label={<Typography>Collaborate With Other Individuals</Typography>}
            labelPlacement="end"
            onChange={(event) => {
              if (!updatedProfile) return;
              const isChecked: boolean = (event.target as HTMLInputElement).checked;
              if (isChecked === updatedProfile.collaborates) return;
              setUpdatedProfile(
                new IndividualProfile({
                  ...updatedProfile,
                  collaborates: isChecked,
                }),
              );
            }}
          />
        </CheckboxContainer>
        <FieldContainer>
          <TitleContainer>
            <Typography variant="h6">Professional Publications</Typography>
            <Tooltip title="Enter a URL to display publications you've written">
              <HelpIcon fontSize="small" />
            </Tooltip>
          </TitleContainer>
          <UrlList
            label="Website URL"
            initialLinks={formProfessionalPublications ?? []}
            onChange={(updatedList: string[]) => {
              setFormProfessionalPublications(updatedList);
              if (!updatedProfile) return;
              if (updatedList === updatedProfile.professionalPublications) return;
              setUpdatedProfile(
                new IndividualProfile({
                  ...updatedProfile,
                  professionalPublications: updatedList,
                }),
              );
            }}
          />
        </FieldContainer>
        {updatedProfile?.categories?.includes("IPAttorney") && (
          <FieldContainer>
            <TitleContainer>
              <Typography variant="h6">Sample Applications</Typography>
              <Tooltip title="Enter a URL address from Espacenet, Google Patents, etc to display applications you've written">
                <HelpIcon fontSize="small" />
              </Tooltip>
            </TitleContainer>
            <UrlList
              label="Website URL or patent number"
              initialLinks={formSampleApplications ?? []}
              onChange={(applicationValues: string[]) => {
                setFormSampleApplications(applicationValues);
                if (!updatedProfile) return;
                if (applicationValues === updatedProfile.sampleApplications) return;
                setUpdatedProfile(
                  new IndividualProfile({
                    ...updatedProfile,
                    sampleApplications: applicationValues,
                  }),
                );
              }}
            />
          </FieldContainer>
        )}
        <FieldContainer>
          <Typography variant="h6">Other Links</Typography>
          <UrlList
            label="Website URL"
            initialLinks={formLinks ?? []}
            onChange={(linkValues: string[]) => {
              setFormLinks(linkValues);
              if (!updatedProfile) return;
              if (linkValues === updatedProfile.links) return;
              setUpdatedProfile(
                new IndividualProfile({
                  ...updatedProfile,
                  links: linkValues,
                }),
              );
            }}
          />
        </FieldContainer>
        <FieldContainer>
          <Typography variant="h6">Resume</Typography>
          <ResumeUpload
            individualId={individualId}
            accept=".doc,.docx,.pdf,.rtf,.odt"
            resume={updatedProfile?.resumeId ?? updatedResume}
            userName={`${updatedProfile?.firstName} ${updatedProfile?.lastName}`}
            onUpload={(updatedResume) => setUpdatedResume(updatedResume)}
            onDelete={() => {
              if(!individualId) {
                setUpdatedResume(null);
                return;
              }
              setUpdatedResume(undefined);
              if(!updatedProfile) return;
              setUpdatedProfile(
                new IndividualProfile({
                  ...updatedProfile,
                  resumeId: undefined
                })
              )
            }}
          />
        </FieldContainer>
      </ContentContainer>
      <Credentials>
        <Typography variant="h6">Credentials</Typography>
        <CredentialsTextField
          id="credentials-textfield"
          value={updatedProfile?.credentials ?? ""}
          onChange={(event) => {
            if (!updatedProfile) return;
            if (event.target.value === updatedProfile.credentials) return;
            setUpdatedProfile(
              new IndividualProfile({
                ...updatedProfile,
                credentials : event.target.value,
              }),
            );
          }}
          multiline={true}
          minRows={4}
          placeholder="Additional info displayed on your profile"
          variant="outlined"
        />
      </Credentials>
      <ProfileDetails>
        {individualId && (
          <VisibilityToggleSwitch
            label="Show Profile On Marketplace"
            checked={isVisible}
            disabled={isSaving || isVisibilitySaving || isLoadingIndividual}
            onChange={(nextIsVisible) => {
              void handleImmediateVisibilityChange(nextIsVisible);
            }}
          />
        )}
      </ProfileDetails>
      <ButtonContainer>
        <LoadingButton
          id="saveButton"
          variant="contained"
          startIcon={<SaveIcon />}
          loading={isSaving}
          color="primary"
          disabled={!canSave}
          onClick={handleSaveButtonClick}
        >
          {individualId ? "Save Changes" : "Create Profile"}
        </LoadingButton>
      </ButtonContainer>
    </MainContainer>
  );
}
